import Log from '@lib/Log'
import Config from '@lib/Config'
import { isPresent } from '@lib/util'
import { getUnixTimestamp } from '@lib/date'
import { getJornayaId } from '@lib/Jornaya'
import { getTimezone } from '@lib/timezone'
import { Eloqua } from '@lib/Eloqua'
import { LeadCache } from '@lib/LeadCache'
import { StoreState } from '@store'
import {
  UsersABConfigType,
  QuotesResult,
  LeadData,
} from './ApiClient.types'
import { hasProgramTypePermission } from './program-mapping'

export * from './ApiClient.types'

/* ApiClient
/* ---------------------------------------------------------------------- */
var domain = '/';
if (typeof window !== 'undefined') {
  domain = 'http://hiq-local.healthiq.com:3000';
  domain = window.location.protocol + '//' + window.location.host;
}

const {
  SUNFIRE_PROSPECT_API_QA,
  SUNFIRE_PROSPECT_API
} = process.env

export const DOMAIN = domain;
export const USER_API_ROOT = '/api/users';
export const API_V2 = '/api/v2';
export const INSURANCE_API_ROOT = `${API_V2}/insurance`;
export const QUIZ_API_ROOT = `${API_V2}/covid_quiz`;
export const QUOTES_API_ROOT = `${API_V2}/insurance/medsup`;
export const QUOTES_LIFE_API_ROOT = `${API_V2}/insurance/hiq_quotes`;
export const SUNFIRE_PROSPECT_API_ROOT = (typeof window !== 'undefined' && window.location.host === 'www.healthiq.com')
  ? SUNFIRE_PROSPECT_API
  : SUNFIRE_PROSPECT_API_QA

// TODO: move URLs to ENV vars
export const HIQ_CRM_API_ROOT =
  (typeof window !== 'undefined' && (window.location.hostname.includes('local') || window.location.hostname.includes('staging')))
  ? 'https://hiq-crm-staging.healthiq.com/special-rate-availability'
  : 'https://hiq-crm.healthiq.com/special-rate-availability'

export const QUESTION_ANSWER_PARAMS = [
  'gender'
];

export const LEAD_PARAMS = [
  'industry_type',
  'email', 'phone_number',
  'name', 'first_name', 'last_name',
  'gender', 'age', 'birthday', 'weight', 'height', 'nicotine',
  'dob', 'dob_day', 'dob_month', 'dob_year',
  'affinity', 'affinity_question', 'affinity_answer',
  'address_street', 'address_city', 'address_state', 'address_zip', 'address_county',
  'income_estimated', 'income_household_estimated', 'mortgage_estimated',
  'intent_time', 'intent_initial',
  'coverage', 'term',
  'existing_policy_life',
  'disclaimer_text', 'disclaimer_signed',
  'ab_test', 'external_ab',
  'utm_source', 'utm_campaign', 'utm_term', 'utm_content', 'utm_medium', 'utm_program',
  'gclid', 'fbclid',
  'product_type',
  'next_followup_time',
  'delay_call',
];

export const ELOQUA_LEAD_PARAMS = [
  'elqFormName',
  'elqCustomerGUID',
  'elqSiteID',
  'elqCookieWrite',
  'email',
  'phone_number',
  'first_name',
  'last_name',
  'address_zip',
  'address_state',
  'dob',
  'dob_day',
  'dob_month',
  'dob_year',
  'age',
  'income_household_estimated',
  'mortgage_estimated',
  'affinity_answer1',
  'affinity_question1'
];


export const LEAD_UPDATE_PARAMS = [
  'first_name', 'last_name',
  'gender',
  'address_street', 'address_city', 'address_state', 'address_zip', 'address_county',
  'referrer',
  'coverage',
];

export const QUOTE_PARAMS = [
  'dob',
  'address_zip',
  'gender',
  'tobacco',
  'effective_date',
  'plan'
];

export const LIFE_QUOTE_PARAMS = [
  'address_state',
  'age',
  'coverage',
  'gender',
  'height',
  'nicotine',
  'term',
  'weight',
  'lead_id',
];

export const USER_PASSWORD_PARAMS = ['email', 'password'];

export const SUNFIRE_PROSPECT_PARAMS = {
  'first_name': 'applicant.firstName',
  'last_name': 'applicant.lastName',
  'gender': 'applicant.gender',
  'dob_month': 'applicant.dob.month',
  'dob_day': 'applicant.dob.day',
  'dob_year': 'applicant.dob.year',
  'address_zip': 'applicant.home.zip',
  'phone_number': 'applicant.phone.primary',
  'email': 'applicant.email',
  'year': 'applicant.partBDate.year',
  'month': 'applicant.partBDate.month',
  'day': 'applicant.partBDate.day',
  'leadId': 'parameters.hiqLeadId',
  'utm_source': 'parameters.utmSource',
  'utm_campaign': 'parameters.utmCampaign',
  'utm_term': 'parameters.utmTerm',
  'utm_content': 'parameters.utmContent',
  'utm_medium': 'parameters.utmMedium',
  'utm_program': 'parameters.utmProgram',
  'utm_call': 'parameters.utmCall',
  'gclid': 'parameters.gclid',
  'fbclid': 'parameters.fbclid'
}

const ApiClient = {
  /* Constants
  /* -------------------------------------------------------------------------------------------- */
  POLL_MAX_ATTEMPTS: 300,
  POLL_TIMEOUT: 1500,

  MAX_KEEP_ALIVE_REQUESTS: 30,
  KEEP_ALIVE_FREQUENCY: 10000,
  numKeepAliveRequests: 0,

  /* Endpoints
  /* -------------------------------------------------------------------------------------------- */
  ENDPOINTS: {
    delayedResponse: {
      show: {
        method: 'GET',
        path: '/delayed_responses/:id'
      },
      showQuotes: {
        method: 'GET',
        path: '/delayed_responses_quotes/:id'
      }
    },
    users: {
      create: {
        api: USER_API_ROOT,
        method: 'POST',
        path: '/'
      },
      fetchCurrentUser: {
        api: USER_API_ROOT,
        method: 'GET',
        path: '/me'
      },
      signIn: {
        api: USER_API_ROOT,
        method: 'POST',
        path: '/sign_in'
      },
      signUp: {
        api: USER_API_ROOT,
        method: 'POST',
        path: '/register'
      },
      resetPassword: {
        api: USER_API_ROOT,
        method: 'POST',
        path: '/password'
      },
      savePassword: {
        api: USER_API_ROOT,
        method: 'PUT',
        path: '/password'
      }
    },
    leads: {
      create: {
        api: INSURANCE_API_ROOT,
        method: 'POST',
        path: '/leads'
      },
      submit: {
        api: '/life-insurance',
        method: 'POST',
        path: '/submit'
      },
      update: {
        api: INSURANCE_API_ROOT,
        method: 'PUT',
        path: '/leads/:id/update'
      },
      busy: {
        api: INSURANCE_API_ROOT,
        method: 'PUT',
        path: '/leads/:id/busy',
      },
      eloqua: {
        api: 'https://s4150963.t.eloqua.com/e/f2',
        method: 'POST',
        path: ''
      }
    },
    covidQuiz: {
      results: {
        api: QUIZ_API_ROOT,
        method: 'POST',
        path: '/results'
      },
      signup: {
        api: QUIZ_API_ROOT,
        method: 'POST',
        path: '/signup'
      }
    },
    quotes: {
      get: {
        api: QUOTES_API_ROOT,
        method: 'POST',
        path: '/quotes'
      },
      life: {
        api: QUOTES_LIFE_API_ROOT,
        method: 'GET',
        path: '/1'
      }
    },
    ab: {
      fetchABConfig: {
        api: API_V2,
        method: 'GET',
        path: '/users/ab_config'
      },
    },
    quiz: {
      fetchQuizzes: {
        api: '/quiz',
        path: '/recent',
        method: 'GET',
      },
    },
    sunfire: {
      prospect: {
        api: SUNFIRE_PROSPECT_API_ROOT,
        method: 'POST',
        path: ''
      },
    },
    hiqCrm: {
      specialRateAvailability: {
        api: HIQ_CRM_API_ROOT,
        method: 'GET',
        path: '/special-rate-availability'
      },
    }
  },

  /* Generic HTTP Request
  /* ------------------------------------------------------------------------------- */
  request(
    method: string,
    api: string,
    path: string,
    data,
    headers: Record<string, string> = {},
    useCsrfToken = false,
    mode?: 'cors' | 'no-cors',
    delayedResponseEndpoint?: Record<string, string>
  ) {
    let url = mode ? api : DOMAIN + api + path;
    const opts: {
      method: string
      headers: Record<string, string>
      body?: string | Blob | ArrayBufferView | ArrayBuffer | FormData | URLSearchParams | ReadableStream<Uint8Array> | null | undefined
      mode?: 'cors' | 'no-cors'
    } = {
      method,
      // credentials: 'include',
      // headers
      //   'Content-Type': 'application/json'
      // }
      headers,
      mode,
    };
    if (data) {
      if (method === 'GET') {
        url += data
      } else {
        opts.body = data;
      }
    }
    if (useCsrfToken) {
      const csrfToken = ApiClient.getCSRFToken();
      if (csrfToken) {
        opts.headers['X-CSRF-Token'] = csrfToken;
      }
    }
    return fetch(url, opts).then((response) => {
      Log.info('ApiClient', `request status: ${response.status}`);
      if (response.status === 500) {
        return {
          error: 'internal',
          message: 'Something went wrong, please try again'
        }
      }

      if (response.status === 302) {
        return {
          success: true,
          message: 'redirected',
        }
      }
      // Breaks error handling everywhere
      // if (response.status === 500 || response.status === 400) {
      //   throw response
      // }

      return response.json().then((result) => {
        if (result && result.polled_request) {
          const pollId = result.id;
          return ApiClient.poll(pollId, 0, delayedResponseEndpoint);
        }

        return result;
      }, (failure) => {
        return { error: 'Invalid Login' }
        // throw new Error(failure)
      });

    }, (error) => {
      Log.info('ApiClient', `request failed: ${error}`);
      // throw new Error(error)
    });
  },

  poll(id, attempt, delayedResponseEndpoint?) {
    if (attempt > ApiClient.POLL_MAX_ATTEMPTS) {
      return {
        error: 'timeout',
        message: 'Something went wrong, please try again'
      };
    }

    const endpoint = delayedResponseEndpoint || ApiClient.ENDPOINTS.delayedResponse.show;
    const path = endpoint.path.replace(':id', id);
    let pollUrl = DOMAIN + path;

    return fetch(pollUrl).then((response) => {
      return response.json().then((result) => {
        if (!result) {
          return {
            error: 'internal',
            message: 'Something went wrong, please try again'
          };
        }
        if (result.status === 'pending') {
          return new Promise((resolve, reject) => {
            setTimeout(() => {
              resolve(ApiClient.poll(id, attempt + 1, delayedResponseEndpoint))
            }, ApiClient.POLL_TIMEOUT);
          });
        }
        return result;
      });
    });
  },

  /* Request Helpers
  /* ------------------------------------------------------------------------------- */
  // TODO: `document.getElementsByName('csrf-token')[0]` has no `content` property. Fix it before enabling this method.
  getCSRFToken() {
    let metaToken;
    if (document.getElementsByName('csrf-token') && document.getElementsByName('csrf-token')[0]) {
      // @ts-ignore
      metaToken = document.getElementsByName('csrf-token')[0].content;
    }
    const cookieToken = ApiClient.getCookie('XSRF-TOKEN');
    return cookieToken || metaToken;
  },
  getCookie(cname) {
    const name = `${cname}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i += 1) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        const v = c.substring(name.length, c.length);
        if (v && v !== 'null') {
          return v;
        }
      }
    }
    return '';
  },

  /* Data Helpers
  /* ------------------------------------------------------------------------------- */
  convertToFormData(data) {
    var formData = new FormData();
    for (var attr in data) {
      let value = data[attr];
      if (value) {
        formData.append(attr, value);
      }
    }
    return formData;
  },

  extractFormParams(data, paramKeys) {
    var formData = new FormData();
    paramKeys.forEach((param) => {
      let value = data[param];
      if (value) {
        formData.append(param, value);
      }
    });
    return formData;
  },

  filterHashData(data, keys) {
    var filtered = {};
    keys.forEach((key) => {
      let value = data[key];
      if (value) {
        filtered[key] = value;
      }
    });
    return filtered;
  },

  extractURLParams(data, paramKeys) {
    var queryParams = '?';
    paramKeys.forEach((param, i) => {
      let value = data[param];
      if (data[param]) {
        queryParams += param + '=' + value + '&';
      }
    });
    return queryParams.substring(0, queryParams.length - 1);
  },

  /* Session Activity Loop (delay phone call)
  /* -------------------------------------------------------------------------------------------- */
  resetKeepAlive() {
    ApiClient.numKeepAliveRequests = 0;
    console.log('[ApiClient] keep alive reset');
  },

  keepSessionActive(leadId) {
    ApiClient.numKeepAliveRequests++;

    ApiClient.leadIsStillBusy(leadId).then((result) => {
      console.log('[ApiClient] busy: ' + ApiClient.numKeepAliveRequests);

      if (ApiClient.numKeepAliveRequests < ApiClient.MAX_KEEP_ALIVE_REQUESTS) {
        setTimeout(function() {
          ApiClient.keepSessionActive(leadId);
        }, ApiClient.KEEP_ALIVE_FREQUENCY);
      } else {
        console.log('[ApiClient] keep alive expired.');
      }
    });
  },
  leadIsStillBusy(id) {
    const endpoint = ApiClient.ENDPOINTS.leads.busy;
    const path = endpoint.path.replace(':id', id);
    return ApiClient.request(endpoint.method, endpoint.api, path, '');
  },

  /* API Functions
  /* ------------------------------------------------------------------------------- */
  signIn(data) {
    const endpoint = ApiClient.ENDPOINTS.users.signIn;

    const params = {
      user: {
        email: data.email,
        password: data.password,
      },
      timezone: getTimezone(),
      platform: 'web',
      app_identifier: 'ssr',
    }

    return ApiClient.request(
      endpoint.method,
      endpoint.api,
      endpoint.path,
      JSON.stringify(params),
      {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    )
  },

  signUp(data: {
    email: string
    password: string
    fullName?: string
    addressStreet?: string
    addressCity?: string
    addressState?: string
    addressZip?: string
    dateOfBirth?: string
    gender?: string
    phoneNumber?: string
  }) {
    const endpoint = ApiClient.ENDPOINTS.users.signUp;
    const params = {
      user: {
        email: data.email,
        password: data.password,
        remember_me: '1',
        name: data.fullName,
        phone_number: data.phoneNumber,
      },
      app_identifier: 'ssr',
      timezone: getTimezone(),
      platform: 'web',
      clientconfigs: '{"email_ab":"", "lead_email_ab":"", "enable_mailchimp": 1}',
      address_street: data.addressStreet,
      address_city: data.addressCity,
      address_state: data.addressState,
      address_zip: data.addressZip,
      birthday: data.dateOfBirth,
      gender: data.gender,
    }

    return ApiClient.request(
      endpoint.method,
      endpoint.api,
      endpoint.path,
      JSON.stringify(params),
      {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    )
  },

  fetchCurrentUser() {
    const endpoint = ApiClient.ENDPOINTS.users.fetchCurrentUser;
    return ApiClient.request(endpoint.method, endpoint.api, endpoint.path, '');
  },

  submitAnswers(data) {
    const endpoint = ApiClient.ENDPOINTS.covidQuiz.results;
    var formData = ApiClient.convertToFormData(data);

    formData.append('last_answer_at', getUnixTimestamp(new Date()).toString());

    return ApiClient.request(endpoint.method, endpoint.api, endpoint.path, formData);
  },

  resetPass(data: { email: string }): Promise<any> {
    const endpoint = ApiClient.ENDPOINTS.users.resetPassword
    const params = {
      user: { email: data.email },
    }

    return ApiClient.request(
      endpoint.method,
      endpoint.api,
      endpoint.path,
      JSON.stringify(params),
      {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    )
  },

  savePassword(data: { email: string, password: string }): Promise<any> {
    const endpoint = ApiClient.ENDPOINTS.users.savePassword

    const formData = ApiClient.extractFormParams(data, USER_PASSWORD_PARAMS)
    formData.append('email', data.email)
    formData.append('password', data.password)

    return ApiClient.request(endpoint.method, endpoint.api, endpoint.path, '')
  },

  async submitLead(leadData: LeadData) {
    const cache = LeadCache.getLeadCache(leadData)

    if (cache) {
      return Promise.resolve(cache)
    }

    const leadResponse = await ApiClient.createLead(leadData);

    if (leadResponse && !leadResponse.error) {
      const hasPermission = hasProgramTypePermission(leadData.industry_type, leadData.product_type)

      if (hasPermission) {
        this.submitLeadToEloqua(leadData)
      }
    }

    return leadResponse
  },

  async submitLeadToEloqua(leadData: LeadData) {
    const endpoint = ApiClient.ENDPOINTS.leads.eloqua;

    const {
      email,
      phone_number,
      first_name,
      last_name,
      address_zip,
      address_state,
      dob_day,
      dob_month,
      dob_year,
      age,
      income_household_estimated,
      mortgage_estimated,
      income_estimated,
      product_type,
      industry_type,
      affinity,
      email_ab,
      existing_policy_life,
      external_ab,
      external_ab2,
      hdyhau_source,
      sms_ab_test,
      tobacco,
      us_citizen,
    } = leadData

    const elqFormName = Eloqua.getFormName(product_type, industry_type)
    const elqCustomerGUID = Eloqua.getCustomerGUID()

    let dob: string | null = null
    if (dob_year && dob_month && dob_day) {
      dob = `${dob_month}/${dob_day}/${dob_year}`
    }

    const formData = ApiClient.extractFormParams(leadData, ELOQUA_LEAD_PARAMS)
    const data = {
      elqFormName,
      elqCustomerGUID,
      elqSiteID: Eloqua.SiteID,
      elqCookieWrite: Eloqua.CookieWrite,
      email: email || undefined,
      phone_number: phone_number || undefined,
      first_name: first_name || undefined,
      last_name: last_name || undefined,
      address_zip: address_zip || undefined,
      address_state: address_state || undefined,
      dob: dob || undefined,
      age: age || undefined,
      income_household_estimated: income_household_estimated || undefined,
      mortgage_estimated: mortgage_estimated || undefined,
      affinity_answer1: income_estimated || undefined,
      affinity_question1: income_estimated ? `What's your individual annual income?` : undefined,

      affinity: affinity || 'healthconscious', // Hardcoded for now.
      email_ab: email_ab || undefined,
      existing_policy_life: existing_policy_life || undefined,
      external_ab: external_ab || undefined,
      external_ab2: external_ab2 || undefined,
      hdyhau_source: hdyhau_source || undefined,
      sms_ab_test: sms_ab_test || undefined,
      tobacco: tobacco || undefined,
      us_citizen: us_citizen || undefined,

      utm_source: Config.utm_source || undefined,
      utm_campaign: Config.utm_campaign || undefined,
      utm_term: Config.utm_term || undefined,
      utm_content: Config.utm_content || undefined,
      utm_medium: Config.utm_medium || undefined,
      utm_program: Config.utm_program || undefined,
    }

    Object.keys(data).forEach(key => {
      if (data[key] !== undefined) {
        formData.append(key, data[key])
      }
    })

    return ApiClient.request(
      endpoint.method,
      endpoint.api,
      endpoint.path,
      formData,
      { 'Content-Type': 'multipart/form-data' },
      false,
      'no-cors'
    )
  },

  createLead(data: LeadData) {
    Log.info(this, 'createLead');

    const endpoint = ApiClient.ENDPOINTS.leads.submit;
    var formData = ApiClient.extractFormParams(data, LEAD_PARAMS);

    if (isPresent(data.selectedQuote)) {
      formData.append('selected_quote', JSON.stringify(data.selectedQuote));
    }

    // if (data.selectedProduct) {
    //   formData.append('product_id', data.selectedProduct.product_id);
    //   formData.append('product_monthly_rate', data.selectedProduct.monthly_rate);
    //   formData.append('product_carrier', data.selectedProduct.company_name);
    //   formData.append('product_term', data.selectedProduct.term);
    //   formData.append('product_coverage', data.selectedProduct.face_value);
    //   formData.append('product_name', data.selectedProduct.product_name);
    //   formData.append('product_underwriting_class', data.selectedProduct.underwriting_class);
    // }

    if (Config.gclid &&
          (Config.utm_source === 'GoogleDisplayNetwork' ||
           Config.utm_source === 'GoogleSearchNetwork')) {
      formData.append('gclid', Config.gclid);
    }

    if (Config.fbclid) {
      formData.append('fbclid', Config.fbclid);
    }

    if (Config.SRC_CD) {
      formData.append('external_lead_id', Config.SRC_CD);
    }

    if (Config.MEDIAALPHA_TOKEN) {
      formData.append('external_lead_id', Config.MEDIAALPHA_TOKEN);
    }

    if (Config.utm_source && Config.utm_source !== '') {
      formData.append('utm_source', Config.utm_source);
    }

    if (Config.utm_campaign && Config.utm_campaign !== '') {
      formData.append('utm_campaign', Config.utm_campaign);
    }

    if (Config.utm_term && Config.utm_term !== '') {
      formData.append('utm_term', Config.utm_term);
    }

    if (Config.utm_content && Config.utm_content !== '') {
      formData.append('utm_content', Config.utm_content);
    }

    if (Config.utm_medium && Config.utm_medium !== '') {
      formData.append('utm_medium', Config.utm_medium);
    }

    if (Config.utm_program && Config.utm_program !== '') {
      formData.append('utm_program', Config.utm_program);
    }

    // Formatting
    if (isPresent(data.dob_month) && isPresent(data.dob_day) && isPresent(data.dob_year)) {
      formData.append('dob', `${data.dob_month}/${data.dob_day}/${data.dob_year}`);
    }

    const industryType = data.industry_type
    formData.append('industry_type', industryType || 'Medsup');

    // Aliases
    if (isPresent(data.income_estimated)) {
      formData.append('affinity_answer1', data.income_estimated || '');
      formData.append('affinity_question1', "What's your individual annual income?");
    }

    // AB tests
    formData.append('ab_test', Config.mode);
    formData.append('lp_url', Config.host + Config.url);

    if (isPresent(data.external_ab)) {
      formData.append('external_ab', data.external_ab || '')
    }

    // TCPA data
    formData.append('jornaya_id', getJornayaId() || '');
    formData.append('disclaimer_signed', Math.trunc(Date.now() / 1000).toString());
    formData.append('disclaimer_text', "By clicking “Continue”, you agree to our Privacy Policy and Terms of Use. You also agree to Health IQ and its agents contacting you with offers for insurance at the phone number you provided, including by text message, autodialed calls, and pre-recorded messages. You can buy a policy without agreeing to these terms by calling (800) 549-1664. You also give Health IQ's licensed sales agents permission to contact you about Medicare Advantage, Medicare Supplement, prescription drug plans, and other health insurance products.");

    // mimic legacy LPs
    formData.append('affinity', 'healthconscious');
    formData.append('context', Config.mode);
    formData.append('form_id', Config.mode);
    formData.append('url_path', 'affinity');
    formData.append('return_type', 'json');

    const promise = ApiClient.request(endpoint.method, endpoint.api, endpoint.path, formData);

    promise.then(res => {
      if (res && !res.error) {
        LeadCache.saveLeadCache(data, res)
      }
    })

    return promise
  },

  updateLead(data) {
    Log.info(this, 'updateLead');

    const endpoint = ApiClient.ENDPOINTS.leads.update;
    var formData = ApiClient.extractFormParams(data, LEAD_UPDATE_PARAMS);
    formData.append('lead_id', data.leadId);

    if (isPresent(data.dob_month) && isPresent(data.dob_day) && isPresent(data.dob_year)) {
      formData.append('dob', `${data.dob_month}/${data.dob_day}/${data.dob_year}`);
    }

    if (isPresent(data.health_quiz)) {
      formData.append('affinity_question2', 'Would you take a 10min quiz in the next 30 days to save on life insurance?');
      formData.append('affinity_answer2', data.health_quiz);
    }

    if (isPresent(data.activity_verification)) {
      formData.append('affinity_question3', 'Would you be willing to provide proof of your exercise to get even greater savings?');
      formData.append('affinity_answer3', data.activity_verification);
    }

    if (isPresent(data.coverage)) {
      formData.append('affinity_question4', 'Would you like to see if you qualify for volume discounts?');
      formData.append('affinity_answer4', data.coverage);
    }

    if (isPresent(data.selectedQuote)) {
      formData.append('selected_quote', JSON.stringify(data.selectedQuote));
    }

    if (isPresent(data.appointmentModes)) {
      formData.append('appointment_modes', JSON.stringify(data.appointmentModes));
    }

    if (isPresent(data.appointmentNotes)) {
      formData.append('appointment_notes', JSON.stringify(data.appointmentNotes));
    }

    const path = endpoint.path.replace(':id', data.leadId)

    return ApiClient.request(endpoint.method, endpoint.api, path, formData);
  },

  formatQuoteResults(results: QuotesResult): QuotesResult {
    // Display only quotes that can be selected.
    results.quotes = results.quotes.filter(quote => quote.allowSelect)

    results.quotes = results.quotes.map(quote => {
      return quote.companyName === 'Americo Fin Life & Ann'
        ? {
          ...quote,
          // Intentional change of the company name.
          companyName: 'Great Southern Life'
        }
        : quote
    })

    return results
  },

  async getQuotes(data): Promise<QuotesResult> {
    const endpoint = ApiClient.ENDPOINTS.quotes.get;
    const drEndpoint = ApiClient.ENDPOINTS.delayedResponse.showQuotes;

    var formData = ApiClient.extractFormParams(data, QUOTE_PARAMS);
    formData.append('lead_id', data.leadId);

    if (isPresent(data.dob_month) && isPresent(data.dob_day) && isPresent(data.dob_year)) {
      formData.append('dob', `${data.dob_month}/${data.dob_day}/${data.dob_year}`);
    }

    const promise = ApiClient.request(endpoint.method, endpoint.api, endpoint.path, formData, {}, false, undefined, drEndpoint);

    return promise.then((results: QuotesResult) => {
      if (results && !results.error && results.quotes) {
        return ApiClient.formatQuoteResults(results)
      }
      return results
    })
  },

  getLifeQuotes(data): Promise<any> {
    const endpoint = ApiClient.ENDPOINTS.quotes.life;
    const drEndpoint = ApiClient.ENDPOINTS.delayedResponse.showQuotes;
    const formData = ApiClient.extractURLParams(data, LIFE_QUOTE_PARAMS);

    return ApiClient.request(endpoint.method, endpoint.api, endpoint.path, formData, {}, false, undefined, drEndpoint);
  },

  fetchABConfig(): Promise<UsersABConfigType> {
    const endpoint = ApiClient.ENDPOINTS.ab.fetchABConfig
    return ApiClient.request(endpoint.method, endpoint.api, endpoint.path, '', {}, true)
  },

  fetchQuizzes(): Promise<any> {
    const endpoint = ApiClient.ENDPOINTS.quiz.fetchQuizzes
    return ApiClient.request(endpoint.method, endpoint.api, endpoint.path, '', {}, true)
  },

  saveSunfireProspect(data: Partial<StoreState>): Promise<any> {
    const endpoint = ApiClient.ENDPOINTS.sunfire.prospect
    const formData = {
      'metadata.partnerId': 88881490,
      'metadata.app': 'ember',
      'metadata.appId': 'hiq',
      'metadata.response': 'json'
    }

    if (data.phone_number) {
      // Strip all non-numeric characters (no formatted number).
      data.phone_number = data.phone_number.replace(/\D/g,'')
    }

    for (let key in data) {
      const sunfireKey = SUNFIRE_PROSPECT_PARAMS[key]
      if (sunfireKey) {
        formData[sunfireKey] = data[key]
      }
    }

    return ApiClient.request(
      endpoint.method,
      endpoint.api as string,
      endpoint.path,
      JSON.stringify(formData),
      { 'Content-Type': 'application/json' },
      false,
      'cors'
    )
  },

  getAutoSpecialRateAvailability(zipcode: string): Promise<any> {
    const endpoint = ApiClient.ENDPOINTS.hiqCrm.specialRateAvailability
    const requestParams = `?zip=${zipcode}`;
    return ApiClient.request(
      endpoint.method,
      endpoint.api as string,
      endpoint.path,
      requestParams,
      {},
      false,
      'cors'
    );
  }
};

export default ApiClient;
